import { useState } from "react";
import { css, styled } from "styled-components";

import { breakpoints } from "common/styles";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { CreateMapError } from "domains/createMapDetail/components/form/CreateMapError";
import { CreateMapTopBar } from "domains/createMapDetail/components/form/CreateMapTopBar";
import { MapInfoInputs } from "domains/createMapDetail/components/form/MapInfoInputs";
import { ImageSection } from "domains/createMapDetail/components/image/ImageSection";
import { interpolate } from "domains/language/interpolate";
import { TopicActionButtons } from "domains/topicDetail/components/actionButtons/TopicActionButtons";
import { TopicDescription } from "domains/topicDetail/components/TopicDescription";
import { getTopicTitle } from "domains/topicDetail/utils";
import { getTmEditMode, getTmSelectedOuterNode, getTmSelectedTopic } from "domains/transformationMap/selectors";
import type { Topic } from "entities/topics/types";

const SidePanelContainer = styled.div`
  margin: 0 1rem;
  padding: 1rem 0;

  @media ${breakpoints.medium} {
    margin: 0 2rem;
    padding: 2rem 0 1rem 0;
  }

  @media ${breakpoints.large} {
    margin: 0;
    padding: 1rem;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;

    ${({ theme }) => css`
      background-color: ${theme.colors.backgroundSecondary};
      border: ${theme.borders.box};
    `}
  }
`;

const StyledText = styled.p`
  margin-top: 0;
  font-size: 1rem;
  line-height: 19px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.fillPrimary};
`;

const StyledCoCurator = styled.div`
  display: inline-block;
  font-size: 1rem;
  font-weight: bold;
`;

const StyledTitle = styled.h2<{ isKeyIssueSelected: boolean }>`
  font-size: ${({ isKeyIssueSelected }) => (isKeyIssueSelected ? 1.4 : 2)}rem;
  font-weight: bold;
  margin: 8px 0 8px;
`;

const StyledConnectionHeader = styled.h3`
  color: ${({ theme }) => theme.colors.fillPrimary};
  font-size: 1.1rem;
  margin: 0 0 5px 0;
`;

const StyledConnectionText = styled.p`
  color: ${({ theme }) => theme.colors.fillPrimary};
  line-height: 1.6rem;
  margin: 0;
`;

type TopicDetailInfoProps = { topic?: Topic };

export const TopicDetailInfo = ({ topic }: TopicDetailInfoProps) => {
  const isEditing = useSelector(getTmEditMode);
  const selectedTopic = useSelector(getTmSelectedTopic);
  const keyIssue = topic?.id !== selectedTopic?.id ? selectedTopic : undefined;
  const outerNode = useSelector(getTmSelectedOuterNode);
  const [isSaved, setSaved] = useState(false);

  const translate = useTranslate();

  const setIsSaved = () => setSaved(!isSaved);

  let summaryField;
  if (isEditing) {
    summaryField = <MapInfoInputs />;
  } else if (topic) {
    summaryField = <TopicDescription topic={topic} selectedTopic={selectedTopic} keyIssue={keyIssue} />;
  }

  const overlay = (
    <>
      <StyledTitle isKeyIssueSelected={!!keyIssue} data-test-id="topic-title">
        {getTopicTitle(topic, selectedTopic)}
      </StyledTitle>
      {topic?.co_curator && (
        <StyledCoCurator>
          {interpolate(translate("app.discover.topic.banner.co_curated_by"), {
            coCurator: () => topic.co_curator,
          })}
        </StyledCoCurator>
      )}

      {topic?.is_custom && topic?.provider?.name && (
        <StyledCoCurator>
          {interpolate(translate("app.discover.topic.banner.co_curated_by"), {
            coCurator: () => topic.provider?.name,
          })}
        </StyledCoCurator>
      )}
    </>
  );

  return (
    <>
      {isEditing && (
        <>
          <CreateMapTopBar setIsSaved={setIsSaved} />
          <CreateMapError isSaved={isSaved} />
        </>
      )}
      <ImageSection overlayElement={overlay} topic={topic} />
      <SidePanelContainer>
        {!outerNode ? (
          <>
            {!isEditing && selectedTopic?.subtitle && <StyledText>{selectedTopic.subtitle}</StyledText>}
            {summaryField}
            {topic && <TopicActionButtons topic={topic} keyIssue={keyIssue} />}
          </>
        ) : (
          <>
            <StyledConnectionHeader>{translate("app.topic.advanced_analytics.summary.title")}</StyledConnectionHeader>
            <StyledConnectionText>
              {interpolate(translate("app.topic.advanced_analytics.summary.context"), {
                topic1: () => <strong>{topic?.name}</strong>,
                topic2: () => <strong>{outerNode?.name}</strong>,
              })}
            </StyledConnectionText>
          </>
        )}
      </SidePanelContainer>
    </>
  );
};
